<template>
  <div>
    <v-card
      class="users-table"
      flat
      tile
    >
      <v-card-title class="pa-0">
        Lista użytkowników
      </v-card-title>

      <v-data-table
        :headers="headers.users"
        :items="users"
        hide-default-footer
        :loading="isLoading"
        must-sort
        fixed-header
        :expanded.sync="expandedUsers"
        show-expand
        height="calc(100vh - 456px)"
        :items-per-page="100"
        :item-class="()=> 'users-table--row'"
        class="custom-table mt-6"
        @update:options="sortUsers"
      >
        <template #expanded-item="{ item }">
          <td
            v-if="item.replacements && item.replacements.length"
            class="pa-0"
            :colspan="headers.users.length + 1"
          >
            <!-- 1 stands for expand switch column -->
            <ReplacementsTable
              :replacements="item.replacements"
              :user-id="item.id"
              @delete="checkExtendedStatus"
            />
          </td>
        </template>
        <template #[`item.data-table-expand`]="{ item, expand, isExpanded }">
          <td>
            <v-btn
              v-if="item.replacements && item.replacements.length"
              icon
              small
              @click="expand(!isExpanded)"
            >
              <v-icon>
                {{ isExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </v-btn>
          </td>
        </template>
        <template #[`item.name`]="{ item }">
          <AppUser :user="item" />
        </template>

        <template #[`item.email`]="{ item }">
          <div :title="item.email">
            {{ item.email }}
          </div>
        </template>

        <template #[`item.roles`]="{ item }">
          <div
            v-for="role in item.roles"
            :key="role.id"
            :title="role.label"
            class="users-table__role overflow-ellipsis"
          >
            {{ role.label }}
          </div>
        </template>

        <template #[`item.createdAt`]="{ item }">
          {{ item.createdAt | dateWithTime }}
        </template>

        <template #[`item.actions`]="{ item }">
          <v-menu
            bottom
            left
            offset-y
          >
            <template #activator="{ on, attrs }">
              <v-btn
                icon
                small
                v-bind="attrs"
                v-on="on"
              >
                <v-icon>mdi-dots-horizontal</v-icon>
              </v-btn>
            </template>

            <v-list min-width="160">
              <v-list-item
                dense
                @click="showRecoverUserPasswordModal(item)"
              >
                Resetuj hasło
              </v-list-item>

              <v-list-item
                dense
                @click="showReplacementModal(item)"
              >
                Dodaj zastępstwo
              </v-list-item>

              <v-list-item
                dense
                @click="showEditUserModal(item)"
              >
                Edycja
              </v-list-item>

              <v-list-item
                dense
                @click="confirmDeleteUser(item)"
              >
                Usuń
              </v-list-item>
            </v-list>
          </v-menu>
        </template>

        <template #no-data>
          Brak dostępnych użytkowników
        </template>
      </v-data-table>
    </v-card>
    <AppPagination
      v-if="users.length"
      @change="changePage"
    />
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import headers from '@/helpers/tableHeaders';
import AppUser from '@/components/UI/AppUser.vue';
import AppPagination from '@/components/UI/AppPagination.vue';
import ReplacementsTable from '@/components/Settings/ReplacementsTable.vue';
import humps from 'humps';

export default {
  components: {
    AppUser,
    AppPagination,
    ReplacementsTable,
  },

  data() {
    return {
      headers,
      expandedUsers: [],
    };
  },

  computed: {
    ...mapState([
      'isLoading',
    ]),

    ...mapState('users', [
      'users',
    ]),
  },

  created() {
    this.setSorting({
      field: 'name',
      direction: 'asc',
    }).then(() => { this.fetchUsers(); });
  },

  beforeDestroy() {
    this.setSorting();
  },

  methods: {
    ...mapActions('users', [
      'fetchUsers',
      'deleteUser',
      'editUserRole',
    ]),

    ...mapActions('dialog', [
      'showDialog',
      'clearDialog',
    ]),

    ...mapActions('filters', [
      'setSorting',
    ]),

    changePage() {
      this.fetchUsers();
      this.$vuetify.goTo(0);
    },

    sortUsers(options) {
      const { sortBy: [field], sortDesc: [desc] } = options;

      if (field) {
        this.setSorting({
          field: humps.decamelize(field),
          direction: desc ? 'desc' : 'asc',
        }).then(() => { this.fetchUsers(); });
      }
    },

    checkExtendedStatus(userId) {
      const isLastReplacement = this.users
        .find((user) => user.id === userId)?.replacements?.length === 1;
      if (isLastReplacement) {
        this.expandedUsers = this.expandedUsers.filter((user) => user.id !== userId);
      }
    },

    confirmDeleteUser(user) {
      this.showDialog({
        header: 'Usuwanie użytkownika',
        text: `Czy na pewno chcesz usunąć użytkownika ${user.firstName || ''} ${user.lastName || ''}?`,
        component: 'Confirm',
        props: {
          confirmText: 'Usuń',
          rejectText: 'Nie usuwaj',
        },
      })
      .then(() => {
        this.deleteUser(user);
      })
      .catch(() => {
        this.clearDialog();
      });
    },

    showRecoverUserPasswordModal(user) {
      this.showDialog({
        header: 'Reset hasła użytkownika',
        component: 'RecoverUserPassword',
        props: { user },
      });
    },

    showEditUserModal(user) {
      this.showDialog({
        header: 'Edycja użytkownika',
        component: 'EditUser',
        props: { user },
      });
    },

    showReplacementModal(user) {
      this.showDialog({
        header: `Zastąp: ${user.firstName} ${user.lastName}`,
        text: 'Wypełnij poniższe dane, aby utworzyć zastępstwo.',
        component: 'ReplacementForm',
        props: { user },
      });
    },
  },

};
</script>

<style lang="scss" scoped>
.users-table {
  padding: 24px 32px;
  &::v-deep .v-data-table__expanded__row {
    background-color: $secondary;
  }
  &::v-deep .users-table--row td {
    border-bottom: 1px solid $secondary !important;
  }
  &::v-deep .users-table--row:nth-of-type(even) {
    background-color: #F8F8F8;
  }
}
</style>
